import React, { useEffect, useState } from "react";
import { getApiCall } from "../../service/httpCall";
import { API_SYSTEM_MONITORING_STATS } from "../../service/url";
import { USER_ROLES } from "../../utilities/constant";
import Loader from "../../components/partials/components/loader";
import { toast } from "react-toastify";
import AsyncStorage from "@react-native-async-storage/async-storage";

import {
  FaServer,
  FaMemory,
  FaHdd,
  FaWifi,
  FaExclamationTriangle,
  FaCheckCircle,
  FaNetworkWired,
} from "react-icons/fa";
import { BsCpuFill, BsHddFill } from "react-icons/bs";

import { Card, Row, Col, Badge, ProgressBar } from "react-bootstrap";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const maskValue = (value) => {
  if (!value) return "";
  const str = value.toString();

  if (str.length <= 3) return str;

  return "*".repeat(str.length - 3) + str.slice(-3);
};

const SystemMonitor = () => {
  const [systemData, setSystemData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const checkUserRole = async () => {
      const rawUser = await AsyncStorage.getItem("user");
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {
        console.error("Invalid user data", e);
      }
    };
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole || loggedInUserRole !== USER_ROLES.SUPERADMIN) return;

    const fetchSystemStats = async () => {
      try {
        const resp = await getApiCall(API_SYSTEM_MONITORING_STATS, {});

        setSystemData(resp);

        setHistory((prev) => [
          ...prev.slice(-19),
          {
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            cpu: parseFloat(resp.system?.cpuLoadPercent || 0),
            memory: parseFloat(resp.system?.usagePercent || 0),
          },
        ]);
      } catch (err) {
        toast.error("Failed to fetch system stats");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemStats();

    const interval = setInterval(fetchSystemStats, 5000);
    return () => clearInterval(interval);
  }, [loggedInUserRole]);

  if (loading || !systemData) return <Loader />;

  const sys = systemData.system || {};
  const disk = systemData.disk || {};
  const pm2Processes = systemData.pm2 || [];
  const network = sys.network || { downloadMbps: "0.00", uploadMbps: "0.00" };
  const connections = sys.connections || {
    established: 0,
    http: 0,
    websocket: 0,
    total: 0,
  };

  const db = sys.db || {
    status: "unknown",
    latencyMs: 0,
    pool: {
      write: { used: 0, idle: 0, max: 0 },
      read: { used: 0, idle: 0, max: 0 },
    },
  };

  const memoryUsedPercent = parseFloat(sys.usagePercent || 0);
  const cpuUsedPercent = parseFloat(sys.cpuLoadPercent || 0);
  const diskUsedPercent = parseFloat(disk.usagePercent || 0);

  const getStatusColor = (percent) => {
    if (percent > 80) return "danger";
    if (percent > 70) return "warning";
    return "success";
  };

  const getStatusIcon = (percent) => {
    if (percent > 80) return <FaExclamationTriangle className="text-danger" />;
    return <FaCheckCircle className="text-success" />;
  };

  const memoryData = [
    { name: "Used", value: memoryUsedPercent, fill: "#0d6efd" },
    { name: "Free", value: 100 - memoryUsedPercent, fill: "#198754" },
  ];

  // PrimeReact templates
  const statusBodyTemplate = (rowData) => (
    <Badge bg={rowData.status === "online" ? "success" : "danger"} pill style={{ textTransform: "capitalize", fontWeight: "800" }}>
      {rowData.status.toUpperCase()}
    </Badge>
  );

  const cpuBodyTemplate = (rowData) => (
    <span className={rowData.cpu > 50 ? "text-danger fw-bold" : ""}>
      {rowData.cpu}%
    </span>
  );

  const memoryBodyTemplate = (rowData) =>
    `${parseFloat(rowData.memoryInMB || 0).toFixed(1)} MB`;

  return (
    <div style={{ marginTop: "50px" }}>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
        <h5 className="mb-0">
          <FaServer className="text-primary" style={{ marginRight: "5px" }} />{" "}
          System Monitor
        </h5>
        <div className="text-muted small d-flex align-items-center gap-2">
          Live • Updates every 5s
        </div>
      </div>

      <Row className="g-4">
        <Col lg={3} md={6}>
          <Card
            className=""
            style={{
              borderRadius: "16px",
              height: "175px",
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted">CPU Usage</h6>
                  <h2 className="mb-0">
                    {cpuUsedPercent.toFixed(1)}
                    <span className="fs-4">%</span>
                  </h2>
                </div>
                <BsCpuFill size={42} className="text-info opacity-75" />
              </div>
              <ProgressBar
                now={cpuUsedPercent}
                variant={getStatusColor(cpuUsedPercent)}
                className="mt-3 rounded-pill"
                style={{ height: "10px" }}
              />
              <div className="mt-2 d-flex align-items-center gap-2">
                {getStatusIcon(cpuUsedPercent)}
                <small
                  className={
                    cpuUsedPercent > 80 ? "text-danger" : "text-success"
                  }
                >
                  {cpuUsedPercent > 80
                    ? "CRITICAL"
                    : cpuUsedPercent > 70
                      ? "HIGH"
                      : "NORMAL"}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6}>
          <Card
            className=""
            style={{
              borderRadius: "16px",
              height: "175px",
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted">Memory Usage</h6>
                  <h2 className="mb-0">
                    {memoryUsedPercent.toFixed(1)}
                    <span className="fs-4">%</span>
                  </h2>
                  <small className="text-muted">
                    {parseFloat(sys.usedMemInMB || 0).toFixed(0)} /{" "}
                    {parseFloat(sys.totalMemInMB || 0).toFixed(0)} MB
                  </small>
                </div>
                <FaMemory size={42} className="text-warning opacity-75" />
              </div>
              <ProgressBar
                now={memoryUsedPercent}
                variant={getStatusColor(memoryUsedPercent)}
                className="mt-3 rounded-pill"
                style={{ height: "10px" }}
              />
              <div className="mt-2 d-flex align-items-center gap-2">
                {getStatusIcon(memoryUsedPercent)}
                <small
                  style={{ fontSize: "13px" }}
                  className={
                    memoryUsedPercent > 80 ? "text-danger" : "text-success"
                  }
                >
                  {memoryUsedPercent > 80 ? "CRITICAL - LOW MEMORY" : "OK"}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6}>
          <Card
            className=""
            style={{
              borderRadius: "16px",
              height: "175px",
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted">Disk Usage</h6>
                  <h2 className="mb-0">
                    {diskUsedPercent.toFixed(1)}
                    <span className="fs-4">%</span>
                  </h2>
                  <small className="text-muted">
                    {disk.usedGB} / {disk.totalGB} GB
                  </small>
                </div>
                <FaHdd size={42} className="text-success opacity-75" />
              </div>
              <ProgressBar
                now={diskUsedPercent}
                variant={getStatusColor(diskUsedPercent)}
                className="mt-3 rounded-pill"
                style={{ height: "10px" }}
              />
              <div className="mt-2 d-flex align-items-center gap-2">
                {getStatusIcon(diskUsedPercent)}
                <small
                  className={
                    diskUsedPercent > 80 ? "text-danger" : "text-success"
                  }
                >
                  {diskUsedPercent > 80 ? "CRITICAL - LOW SPACE" : "OK"}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6}>
          <Card
            className=""
            style={{
              borderRadius: "16px",
              height: "175px",
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted">Network Throughput</h6>

                  <div className="mt-2">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="text-success">↓</span>
                      <h3 className="mb-0 fw-semibold">
                        {parseFloat(network.downloadMbps).toFixed(1)}
                        <span className="fs-5 text-muted"> Mbps</span>
                      </h3>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-info">↑</span>
                      <h3 className="mb-0 fw-semibold">
                        {parseFloat(network.uploadMbps).toFixed(1)}
                        <span className="fs-5 text-muted"> Mbps</span>
                      </h3>
                    </div>
                  </div>
                </div>

                <FaNetworkWired
                  size={42}
                  className="text-primary opacity-75 mt-0"
                />
              </div>

              <div className="mt-2 d-flex align-items-center gap-2">
                <FaWifi className="text-primary" />
                <small className="text-muted">
                  {connections.total} active connections
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="mt-0">
        <Col lg={8}>
          <Card
            className="pt-2"
            style={{
              borderRadius: "16px",
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            }}
          >
            <Card.Header className="bg-transparent">
              <h5>Resource Usage Trend (Last 2 Minutes)</h5>
            </Card.Header>
            <Card.Body style={{ height: "320px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line
                    type="natural"
                    dataKey="cpu"
                    stroke="var(--bs-info)"
                    strokeWidth={3}
                    dot={false}
                    name="CPU %"
                  />
                  <Line
                    type="natural"
                    dataKey="memory"
                    stroke="var(--bs-warning)"
                    strokeWidth={3}
                    dot={false}
                    name="Memory %"
                  />
                  <Legend verticalAlign="bottom" height={10} />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card
            className="pt-2"
            style={{
              borderRadius: "16px",
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            }}
          >
            <Card.Header className="bg-transparent">
              <h5>Memory Distribution</h5>
            </Card.Header>
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <ResponsiveContainer width="100%" height={205}>
                <PieChart>
                  <Pie
                    data={memoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={85}
                    dataKey="value"
                  >
                    {memoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={10} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 text-center">
                <h3>{memoryUsedPercent.toFixed(1)}%</h3>
                <p className="text-muted mb-0">Used Memory</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* {pm2Processes.length > 0 && (
        <Card
          className="mt-0 shadow-sm pt-2 pb-4"
          style={{ borderRadius: "16px", width: "100%", overflowX: "scroll" }}
        >
          <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <BsHddFill /> Running Processes (PM2)
            </h5>
          </Card.Header>
          <Card.Body>
            <DataTable value={pm2Processes} emptyMessage="No processes found">
              <Column field="name" header="Process Name" body={(rowData) => maskValue(rowData.name)} sortable />
              <Column
                field="status"
                header="Status"
                body={statusBodyTemplate}
              />
              <Column
                field="cpu"
                header="CPU (%)"
                body={cpuBodyTemplate}
                align="right"
              />
              <Column
                field="memoryInMB"
                header="Memory (MB)"
                body={memoryBodyTemplate}
                align="right"
              />
              <Column field="restarts" header="Restarts" align="right" />
            </DataTable>
          </Card.Body>
        </Card>
      )} */}
      <Row className="mt-4">
        <Col lg={12}>
          <Card
            style={{
              borderRadius: "16px",
              boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 d-flex align-items-center gap-2">
                  <FaServer className="text-primary" /> Database Status
                </h5>

                <Badge
                  bg={db.status === "connected" ? "success" : "danger"}
                  style={{ fontSize: "14px", padding: "6px 10px" }}
                >
                  {db.status.toUpperCase()}
                </Badge>
              </div>

              <Row>
                <Col md={3}>
                  <h6 className="text-muted">Latency</h6>
                  <h3 className="mb-0 text-info">
                    {db.latencyMs} <span className="fs-5 text-muted">ms</span>
                  </h3>
                </Col>

                <Col md={3}>
                  <h6 className="text-muted">Write Pool</h6>
                  <div className="small text-muted">Used / Idle / Max</div>
                  <h5 className="mb-0 text-danger">
                    {db.pool.write.used} / {db.pool.write.idle} /{" "}
                    {db.pool.write.max}
                  </h5>
                </Col>

                <Col md={3}>
                  <h6 className="text-muted">Read Pool</h6>
                  <div className="small text-muted">Used / Idle / Max</div>
                  <h5 className="mb-0 text-success">
                    {db.pool.read.used} / {db.pool.read.idle} /{" "}
                    {db.pool.read.max}
                  </h5>
                </Col>

                <Col md={3}>
                  <h6 className="text-muted">Total Connections</h6>
                  <h3 className="mb-0">
                    {(db.pool.write.used || 0) + (db.pool.read.used || 0)}
                  </h3>
                </Col>
              </Row>

              <div className="mt-3 d-flex align-items-center gap-2">
                {db.status === "connected" ? (
                  <FaCheckCircle className="text-success" />
                ) : (
                  <FaExclamationTriangle className="text-danger" />
                )}
                <small className="text-muted">
                  {db.status === "connected"
                    ? "Database is healthy and responding"
                    : "Database connection issue detected"}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SystemMonitor;
